import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { HelpdeskTaskTypeFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/task-type/helpdesk-task-type-find.dto';
import {
  HelpdeskTaskTypeFormDto,
  type HelpdeskTaskTypeFormInterface
} from '@zaoblako/shared/modules/helpdesk/dto/task-type/helpdesk-task-type-form.dto';
import { HelpdeskTaskPriorityEnum } from '@zaoblako/shared/modules/helpdesk/enum/helpdesk.task.priority.enum';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { DirectionService } from '@zaoblako/web/core/services/finance/direction.service';
import { HelpDeskTaskTypeService } from '@zaoblako/web/core/services/helpdesk/help-desk-task-type.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { DirectionSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/direction-single-select/direction-single-select.component';
import { EavEntityTypeSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/eav-entity-type-single-select/eav-entity-type-single-select.component';
import { UserGroupMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-group-multi-select/user-group-multi-select.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { UserSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-single-select/user-single-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { HelpdeskTaskPriorityNamePipe } from '@zaoblako/web/shared/pipes/helpdesk-task-priority-name.pipe';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { InputNumberModule } from 'primeng/inputnumber';
import { CheckboxModule } from 'primeng/checkbox';
import { Observable } from 'rxjs';
import { AiAgentSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/ai-agent-single-select/ai-agent-single-select.component';
import { AgentFindInterface } from '@zaoblako/shared/modules/ai/dtos/agent/agent-find.dto';

@Component({
  standalone: true,
  selector: 'crm-helpdesk-task-type-form',
  templateUrl: './helpdesk-task-type-form.component.html',
  imports: [
    CommonModule,
    FormsModule,
    SpinnerComponent,
    InputTextComponent,
    JoinPipe,
    InputNumberModule,
    SingleSelectComponent,
    HelpdeskTaskPriorityNamePipe,
    DirectionSingleSelectComponent,
    ButtonComponent,
    CardModule,
    UserGroupMultiSelectComponent,
    UserSingleSelectComponent,
    EavEntityTypeSingleSelectComponent,
    AiAgentSingleSelectComponent,
    CheckboxModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HelpdeskTaskTypeFormComponent extends BaseFormComponent<HelpdeskTaskTypeFormInterface> implements OnInit {
  @Input() public readonly index: number;
  @Input() public saveButtonLabel = 'Сохранить';

  public taskType: HelpdeskTaskTypeFindInterface = <HelpdeskTaskTypeFindInterface>{};

  public dto = HelpdeskTaskTypeFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public directions: DirectionFindInterface[] = [];

  public priorities = Object.keys(HelpdeskTaskPriorityEnum);

  public constructor(
    private readonly helpDeskTaskTypeService: HelpDeskTaskTypeService,
    private readonly formStoreService: FormStoreService,
    private readonly directionService: DirectionService,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    this.directionService.find<DirectionFindInterface>().subscribe((directions) => {
      this.directions = directions;
      this.cdRef.markForCheck();
    });

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById(): void {
    this.formStoreService.setLoading(true, this.index);
    this.helpDeskTaskTypeService
      .findById<HelpdeskTaskTypeFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (taskType) => {
          this.taskType = taskType;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveTaskType(): void {
    if (this.checkValidate(this.taskType).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.helpDeskTaskTypeService
          .update<HelpdeskTaskTypeFormInterface, HelpdeskTaskTypeFindInterface>(this.taskType)
          .subscribe({
            next: (taskType) => {
              this.formStoreService.setLoading(false, this.index);
              this.errorService.addSuccessMessage(`Тип заявки "${this.taskType.name}" обновлен`);
              this.formStoreService.saveNext(taskType, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.helpDeskTaskTypeService.create<HelpdeskTaskTypeFindInterface>(this.taskType).subscribe({
          next: (filial) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Тип заявки создан');
            this.formStoreService.saveNext(filial, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteTaskType(): void {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить тип заявки "${this.taskType.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.helpDeskTaskTypeService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.errorService.addSuccessMessage(`Тип заявки "${this.taskType.name}" удален`);
            this.formStoreService.deleteNext(this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }

  public restoreTaskType(): void {
    this.confirmService.confirm({
      message: `Вы действительно хотите восстановить тип заявки "${this.taskType.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.helpDeskTaskTypeService.restoreOne(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.taskType = { ...this.taskType, deleted: null };
            this.cdRef.markForCheck();
            this.errorService.addSuccessMessage(`Тип заявки "${this.taskType.name}" восстановлен`);
            this.formStoreService.saveNext(this.taskType, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }

  public changeAiAgent(agent: AgentFindInterface) {
    if (!agent) {
      this.taskType.autoReplyHours = null;
    }
  }
}
